import { useMemo, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { useBahan, usePicuScraping } from '../../../domains/content/api'
import { tanggalJam } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, BilahTabel, Chip, Gagal, Kosong, Memuat, Panel, Tabel, Tombol,
} from '../../../shared/ui'

/**
 * BAHAN — "kenapa kontennya begitu".
 *
 * Kolom `score_reason` sudah dihitung dan disimpan sistem sejak awal
 * (`trending_topics.score_reason`), dan sampai 10 September TIDAK ADA SATU LAYAR PUN yang
 * membacanya. Ia yang menjawab kenapa konten otomatis memilih berita tertentu — dan kalau
 * hasilnya terasa melenceng, sebabnya biasanya kata kunci yang terlalu luas, bukan
 * modelnya yang keliru.
 *
 * SKOR RENDAH TIDAK DIBUANG, hanya dipudarkan. Penilaian mesin bagus untuk memilih
 * rata-rata; manusia yang tahu bahwa satu berita bernilai rendah kebetulan menyangkut
 * calon klien besar minggu ini. Layar yang menyaringnya menghapus penilaian itu dari
 * tangan orang yang punya konteksnya.
 */
/** Nama situsnya saja — "detik.com", bukan URL sepanjang dua baris. */
function sumberDari(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

export default function Bahan({ onPakai }) {
  const { t, locale } = useBahasa()
  const q = useBahan(50)
  const picu = usePicuScraping()
  const [cari, setCari] = useState('')

  // Disaring di KLIEN: rutenya mengembalikan 50 teratas dan tidak menerima parameter
  // pencarian. Benar selama puluhan; begitu ratusan, penyaringnya harus pindah ke server
  // dan pemanggilan ini ikut berubah. Ditulis supaya perpindahannya tidak jadi kejutan.
  const tersaring = useMemo(() => {
    const c = cari.trim().toLowerCase()
    const semua = q.data ?? []
    if (!c) return semua
    return semua.filter((b) => [b.title, b.keyword, b.score_reason]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(c)))
  }, [q.data, cari])

  return (
    <Panel
      judul={t('bh.judul')}
      catatan={t('bh.catatan')}
      aksi={
        <Tombol disabled={picu.isPending} onClick={() => picu.mutate()}>
          {picu.isPending ? t('bh.mencari') : t('bh.cariSekarang')}
        </Tombol>
      }
    >
      {picu.isError && (
        <Bendera nada="bad" role="alert" className="mx-3.5 mt-3">{picu.error?.message}</Bendera>
      )}

      {q.isLoading && <Memuat tinggi={6} />}
      {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

      {q.data && q.data.length > 0 && (
        <BilahTabel
          cari={{ nilai: cari, set: setCari, placeholder: t('bh.cariPlaceholder') }}
          ringkas={t('umum.dari', {
            mulai: tersaring.length ? 1 : 0, akhir: tersaring.length, total: q.data.length,
          })}
        />
      )}

      {q.data && (tersaring.length === 0 ? (
        <Kosong
          judul={cari ? t('bh.tidakCocok') : t('bh.kosong')}
          keterangan={cari ? undefined : t('bh.kosongKet')}
        />
      ) : (
        <>
          <Tabel
            kepala={[t('bh.kolSkor'), t('bh.kolBerita'), t('bh.kolKataKunci'),
                     t('bh.kolWaktu'), '']}
          >
            {tersaring.map((b) => {
              // Ambang 0,5 cuma untuk MEMUDARKAN, bukan menyembunyikan — barisnya tetap
              // lengkap dan tetap bisa dibaca.
              const rendah = (b.score ?? 0) < 0.5
              return (
                <Baris key={b.id}>
                  {/* SKOR DI DEPAN, dan berwarna. Ia yang menentukan apakah baris ini
                      layak dibaca sama sekali; menaruhnya di kolom ketiga membuat mata
                      membaca judul dulu lalu mundur. */}
                  <td className="tabular">
                    <Chip nada={rendah ? 'idle' : (b.score ?? 0) >= 0.7 ? 'ok' : 'warn'}
                          titik={false}>
                      {(b.score ?? 0).toFixed(2)}
                    </Chip>
                  </td>
                  {/* JUDUL, ALASAN, DAN SUMBER JADI SATU KOLOM.
                      Alasan sebagai kolom penuh selebar 46ch memaksa seluruh tabel melebar
                      dan mendorong kolom kanan keluar layar — padahal ia kalimat panjang
                      yang dibaca SESUDAH judulnya, bukan sejajar dengannya. Rancangan
                      7 September menumpuknya, dan itu yang benar. */}
                  <td className={rendah ? 'text-ink3 max-w-[52ch]' : 'max-w-[52ch]'}>
                    {b.url ? (
                      <a
                        href={b.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-ink no-underline hover:underline"
                      >
                        {b.title}
                      </a>
                    ) : <b className="font-semibold">{b.title}</b>}
                    {b.score_reason && (
                      <span className="block text-[11px] text-ink2 leading-snug">
                        {b.score_reason}
                      </span>
                    )}
                    {b.url && (
                      <span className="block font-mono text-[10.5px] text-ink3">
                        {sumberDari(b.url)}
                      </span>
                    )}
                  </td>
                  <td>
                    {b.keyword
                      ? <Chip nada="idle" titik={false}>{b.keyword}</Chip>
                      : <span className="text-ink3">—</span>}
                  </td>
                  <td className="font-mono text-[11px] text-ink2 whitespace-nowrap">
                    {b.created_at ? tanggalJam(b.created_at, locale) : '—'}
                  </td>
                  {/* JALAN PINTAS KE FORMULIR, bukan sekadar tombol.
                      Tanpa ini, memakai sebuah bahan berarti menyalin judulnya dengan
                      tangan lalu pindah tab — dan yang tersalin biasanya cuma judulnya,
                      sementara ALASAN mesin memilihnya tertinggal di layar ini. Padahal
                      alasan itu yang membuat captionnya menyinggung hal yang benar. */}
                  <td className="whitespace-nowrap text-right">
                    <Tombol kecil jenis="sekunder" onClick={() => onPakai?.(b)}>
                      {t('bh.buatDariIni')}
                    </Tombol>
                  </td>
                </Baris>
              )
            })}
          </Tabel>
          <p className="m-0 px-3.5 py-2.5 text-[11.5px] text-ink2 border-t border-line
                        max-w-[80ch]">
            {t('bh.kaki')}
          </p>
        </>
      ))}
    </Panel>
  )
}
